import React, { useRef, useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import './CTA.css';
import { useContactModal } from '../../../stores/modalStore';

// Remove empty interface since we don't need props anymore

// Interactive Mesh Component
function InteractiveMesh({
  children,
  position,
  rotation,
  hoverScale = 1.2,
  speed = 1,
}: {
  children: React.ReactNode;
  position: [number, number, number];
  rotation: [number, number, number];
  hoverScale?: number;
  speed?: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);
  const baseScale = useRef(1);

  useFrame((state) => {
    if (meshRef.current) {
      // Gentle floating animation
      meshRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.1;

      // Continuous rotation
      meshRef.current.rotation.x =
        rotation[0] + state.clock.elapsedTime * speed * 0.2;
      meshRef.current.rotation.y =
        rotation[1] + state.clock.elapsedTime * speed * 0.3;
      meshRef.current.rotation.z =
        rotation[2] + state.clock.elapsedTime * speed * 0.1;

      // Scale animation
      const targetScale = active
        ? hoverScale
        : hovered
        ? hoverScale * 0.8
        : baseScale.current;
      meshRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.05
      );

      // Color animation on hover
      if (meshRef.current.material) {
        const material = meshRef.current.material as THREE.MeshStandardMaterial;
        if (hovered) {
          material.opacity = Math.sin(state.clock.elapsedTime * 8) * 0.1 + 0.5;
        } else {
          material.opacity = 0.25;
        }
      }
    }
  });

  return (
    <mesh
      ref={meshRef}
      position={position}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
        baseScale.current = 1;
      }}
      onPointerDown={() => {
        setActive(true);
        baseScale.current = hoverScale;
      }}
      onPointerUp={() => {
        setActive(false);
        baseScale.current = 1;
      }}
    >
      {children}
    </mesh>
  );
}

// DNA Double Helix Component
function DNAHelix({
  position,
  hoverScale = 1.3,
  speed = 1.2,
}: {
  position: [number, number, number];
  hoverScale?: number;
  speed?: number;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);
  const baseScale = useRef(1);

  useFrame((state) => {
    if (groupRef.current) {
      // Gentle floating
      groupRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.08;

      // Rotation around helix axis
      groupRef.current.rotation.y = state.clock.elapsedTime * speed * 0.5;

      // Scale animation
      const targetScale = active
        ? hoverScale
        : hovered
        ? hoverScale * 0.8
        : baseScale.current;
      groupRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.05
      );
    }
  });

  // Create DNA helix strands
  const helixPoints: [number, number, number][] = [];
  const numPoints = 20;
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * Math.PI * 4; // Two full turns
    const radius = 0.3;
    helixPoints.push([
      Math.cos(angle) * radius,
      (i / numPoints) * 2 - 1, // Height from -1 to 1
      Math.sin(angle) * radius,
    ]);
  }

  return (
    <group ref={groupRef} position={position}>
      {/* DNA Backbone */}
      {helixPoints.slice(0, -1).map((point, i) => (
        <line key={i}>
          <bufferGeometry>
            <bufferAttribute
              attach='attributes-position'
              args={[
                new Float32Array([
                  point[0],
                  point[1],
                  point[2],
                  helixPoints[i + 1][0],
                  helixPoints[i + 1][1],
                  helixPoints[i + 1][2],
                ]),
                3,
              ]}
            />
          </bufferGeometry>
          <lineBasicMaterial
            color='#2BB6A3'
            transparent
            opacity={0.6}
            linewidth={2}
          />
        </line>
      ))}

      {/* Base pairs (connectors) */}
      {helixPoints.map((point, i) => {
        const oppositePoint = [-point[0], point[1], -point[2]];
        return (
          <line key={`base-${i}`}>
            <bufferGeometry>
              <bufferAttribute
                attach='attributes-position'
                args={[
                  new Float32Array([
                    point[0],
                    point[1],
                    point[2],
                    oppositePoint[0],
                    oppositePoint[1],
                    oppositePoint[2],
                  ]),
                  3,
                ]}
              />
            </bufferGeometry>
            <lineBasicMaterial
              color='#d4457c'
              transparent
              opacity={0.4}
              linewidth={1}
            />
          </line>
        );
      })}
    </group>
  );
}

// Medical Data Chart Component
function DataChart({
  position,
  hoverScale = 1.4,
  speed = 1.5,
}: {
  position: [number, number, number];
  hoverScale?: number;
  speed?: number;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.1;

      const targetScale = active ? hoverScale : hovered ? hoverScale * 0.8 : 1;
      groupRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.05
      );

      // Gentle rotation
      groupRef.current.rotation.z =
        Math.sin(state.clock.elapsedTime * speed * 0.3) * 0.1;
    }
  });

  // Chart bars data
  const bars = [0.8, 1.2, 0.6, 1.4, 1.0, 0.9, 1.1];

  return (
    <group ref={groupRef} position={position}>
      {/* Chart bars */}
      {bars.map((height, i) => (
        <mesh key={i} position={[i * 0.4 - 1.2, height / 2, 0]}>
          <boxGeometry args={[0.2, height, 0.2]} />
          <meshStandardMaterial
            color='#2BB6A3'
            transparent
            opacity={0.4}
            wireframe
          />
        </mesh>
      ))}

      {/* Chart line connecting peaks */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach='attributes-position'
            args={[
              new Float32Array([
                -1.2,
                bars[0],
                0,
                -0.8,
                bars[1],
                0,
                -0.4,
                bars[2],
                0,
                0,
                bars[3],
                0,
                0.4,
                bars[4],
                0,
                0.8,
                bars[5],
                0,
                1.2,
                bars[6],
                0,
              ]),
              3,
            ]}
          />
        </bufferGeometry>
        <lineBasicMaterial
          color='#d4457c'
          transparent
          opacity={0.8}
          linewidth={3}
        />
      </line>
    </group>
  );
}

// Molecular Structure Component
function MoleculeModel({
  position,
  hoverScale = 1.5,
  speed = 1.1,
}: {
  position: [number, number, number];
  hoverScale?: number;
  speed?: number;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.09;

      const targetScale = active ? hoverScale : hovered ? hoverScale * 0.8 : 1;
      groupRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.05
      );

      // Atom rotation
      groupRef.current.rotation.y = state.clock.elapsedTime * speed * 0.3;
      groupRef.current.rotation.x =
        Math.sin(state.clock.elapsedTime * speed) * 0.1;
    }
  });

  // Atoms positions (simplified molecule)
  const atoms: [number, number, number][] = [
    [0, 0, 0], // Center
    [1, 0.5, 0], // Right-top
    [-1, 0.5, 0], // Left-top
    [0.5, -1, 0], // Right-bottom
    [-0.5, -1, 0], // Left-bottom
    [0, 0, 1], // Front
    [0, 0, -1], // Back
  ];

  return (
    <group ref={groupRef} position={position}>
      {/* Atoms */}
      {atoms.map((atomPos, i) => (
        <mesh key={i} position={atomPos as [number, number, number]}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial
            color={i === 0 ? '#d4457c' : '#2BB6A3'}
            transparent
            opacity={0.7}
          />
        </mesh>
      ))}

      {/* Bonds */}
      {atoms.slice(1).map((atomPos, i) => (
        <line key={`bond-${i}`}>
          <bufferGeometry>
            <bufferAttribute
              attach='attributes-position'
              args={[
                new Float32Array([0, 0, 0, atomPos[0], atomPos[1], atomPos[2]]),
                3,
              ]}
            />
          </bufferGeometry>
          <lineBasicMaterial
            color='#424242'
            transparent
            opacity={0.5}
            linewidth={2}
          />
        </line>
      ))}
    </group>
  );
}

// Data Network Component
function DataNetwork({
  position,
  hoverScale = 1.3,
  speed = 0.8,
}: {
  position: [number, number, number];
  hoverScale?: number;
  speed?: number;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const [active, setActive] = useState(false);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.07;

      const targetScale = active ? hoverScale : hovered ? hoverScale * 0.8 : 1;
      groupRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.05
      );

      // Network rotation
      groupRef.current.rotation.y = state.clock.elapsedTime * speed * 0.4;
    }
  });

  // Network nodes
  const nodes: [number, number, number][] = [
    [0, 0, 0],
    [-1, 1, 0],
    [1, 1, 0],
    [-1, -1, 0],
    [1, -1, 0],
    [0, 0, 1],
    [0, 0, -1],
  ];

  return (
    <group ref={groupRef} position={position}>
      {/* Network nodes */}
      {nodes.map((nodePos, i) => (
        <mesh key={i} position={nodePos as [number, number, number]}>
          <sphereGeometry args={[0.1, 12, 12]} />
          <meshStandardMaterial color='#2BB6A3' transparent opacity={0.8} />
        </mesh>
      ))}

      {/* Network connections */}
      {nodes.map((node, i) =>
        nodes.slice(i + 1).map((otherNode, j) => (
          <line key={`connection-${i}-${j}`}>
            <bufferGeometry>
              <bufferAttribute
                attach='attributes-position'
                args={[
                  new Float32Array([
                    node[0],
                    node[1],
                    node[2],
                    otherNode[0],
                    otherNode[1],
                    otherNode[2],
                  ]),
                  3,
                ]}
              />
            </bufferGeometry>
            <lineBasicMaterial
              color='#d4457c'
              transparent
              opacity={0.3}
              linewidth={1}
            />
          </line>
        ))
      )}
    </group>
  );
}

const CTA: React.FC = () => {
  const { open: openContactModal } = useContactModal();

  return (
    <motion.section 
      className='cta-section professional-cta centered'
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div>
        {/* Animated Background Design */}
        <div className='cta-animated-bg'>
          <div className='floating-shapes'>
            <div className='shape shape-1'></div>
            <div className='shape shape-2'></div>
            <div className='shape shape-3'></div>
            <div className='shape shape-4'></div>
            <div className='shape shape-5'></div>
            <div className='shape shape-6'></div>
          </div>
          <div className='neural-connections'>
            <div className='connection connection-1'></div>
            <div className='connection connection-2'></div>
            <div className='connection connection-3'></div>
            <div className='connection connection-4'></div>
          </div>
          <div className='data-particles'>
            <div className='particle p1'></div>
            <div className='particle p2'></div>
            <div className='particle p3'></div>
            <div className='particle p4'></div>
            <div className='particle p5'></div>
            <div className='particle p6'></div>
            <div className='particle p7'></div>
            <div className='particle p8'></div>
          </div>
        </div>

        {/* 3D Primitives Scene */}
        <div className='cta-3d-scene'>
          <Suspense fallback={<div className="component-loader">Loading 3D scene...</div>}>
            <Canvas camera={{ position: [0, 0, 8], fov: 50 }}>
              <ambientLight intensity={0.4} />
              <directionalLight position={[10, 10, 5]} intensity={0.8} />
              <pointLight
                position={[-10, -10, -5]}
                intensity={0.6}
                color='#2BB6A3'
              />

              {/* DNA Double Helix */}
              <DNAHelix position={[7, 3, -2]} hoverScale={1.3} speed={1.2} />

              {/* Clinical Trial Molecule */}
              <MoleculeModel
                position={[-12, -4.2, -4]}
                hoverScale={1.5}
                speed={1.1}
              />

              {/* Health Data Network */}
              <DataNetwork position={[-4, 5, -10]} hoverScale={1.3} speed={0.8} />
            </Canvas>
          </Suspense>
        </div>
      </div>
      <div className='container'>
        <div className='cta-content-centered'>
          <h2 className='cta-headline'>
            Are You <span className='gradient-text'>Ready</span> to Take a Step?
          </h2>
          <p className='cta-description'>
            Work with Cinquity and transform your clinical trial operations.
            Whether you need dedicated eCOA consulting, centralized license
            management, or complete end-to-end clinical trial support, we're
            here to help.
          </p>
          <button className='cta-demo-btn' onClick={openContactModal}>
            Request a Demo
          </button>
        </div>
      </div>
    </motion.section>
  );
};

export default CTA;
